import { useState } from 'react';
import { useAssets, useGoals } from '../../hooks/useApi';
import { AddAssetModal } from '../../components/AddAssetModal/AddAssetModal';
import { AssetDetailModal } from '../../components/AssetDetailModal/AssetDetailModal';
import { formatAmount } from '../../utils/numberFormat';
import { Country, Currency, AssetAccount, ACCOUNT_TYPE_LABELS, COUNTRY_CURRENCIES } from '../../types/api.types';
import styles from './DashboardPage.module.scss';

type CountryTab = 'india' | 'us' | 'canada';

const countryTabs: { id: CountryTab; label: string; country: Country }[] = [
    { id: 'india', label: 'India', country: 'INDIA' },
    { id: 'us', label: 'United States', country: 'US' },
    { id: 'canada', label: 'Canada', country: 'CAN' },
];

function formatBalance(balance: number, currency: Currency): string {
    return formatAmount(balance, currency);
}

function getCountryTotal(assets: AssetAccount[], country: Country): number {
    return assets.filter((a) => a.country === country).reduce((sum, a) => sum + a.balance, 0);
}

function getWeightedReturn(assets: AssetAccount[], country: Country): number | null {
    const countryAssets = assets.filter((a) => a.country === country);
    const totalBalance = countryAssets.reduce((sum, a) => sum + a.balance, 0);
    if (totalBalance === 0) return null;
    return countryAssets.reduce((sum, a) => sum + a.expectedReturn * (a.balance / totalBalance), 0);
}

export function DashboardPage() {
    const [activeTab, setActiveTab] = useState<CountryTab>('india');
    const [showAddAsset, setShowAddAsset] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<AssetAccount | null>(null);
    const { assets, loading, error, refetch, updateAsset, deleteAsset } = useAssets();
    const { goals } = useGoals();

    const activeCountry = countryTabs.find((t) => t.id === activeTab)!.country;
    const filteredAssets = assets.filter((a) => a.country === activeCountry);
    const total = getCountryTotal(assets, activeCountry);
    const weightedReturn = getWeightedReturn(assets, activeCountry);
    const currency = COUNTRY_CURRENCIES[activeCountry];

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading your assets...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={refetch}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Accounts Overview</h1>
                    <p className={styles.subtitle}>Your cross-border financial inventory.</p>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.tabs}>
                        {countryTabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <button className={styles.addButton} onClick={() => setShowAddAsset(true)}>
                        + Add Asset
                    </button>
                </div>
            </div>

            <div className={styles.totalCard}>
                <span className={styles.totalLabel}>TOTAL {activeCountry} ASSETS</span>
                <div className={styles.totalValue}>
                    <span className={styles.amount}>{formatBalance(total, currency)}</span>
                    <span className={styles.currency}>{currency}</span>
                    {weightedReturn !== null && (
                        <span className={styles.changePositive}>+{weightedReturn.toFixed(1)}% Annualized</span>
                    )}
                </div>
                <span className={styles.syncTime}>{filteredAssets.length} account(s)</span>
            </div>

            {filteredAssets.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No assets in this country yet.</p>
                    <button className={styles.addButton} onClick={() => setShowAddAsset(true)}>
                        + Add your first asset
                    </button>
                </div>
            ) : (
                <div className={styles.assetsGrid}>
                    {filteredAssets.map((asset) => (
                        <div key={asset.accountId} className={styles.assetCard} onClick={() => setSelectedAsset(asset)}>
                            <div className={styles.assetHeader}>
                                <span className={styles.assetName}>{asset.label}</span>
                                <span className={styles.returnBadge}>+{asset.expectedReturn}%</span>
                            </div>
                            <div className={styles.assetMeta}>
                                <span className={styles.assetType}>{ACCOUNT_TYPE_LABELS[asset.accountType]}</span>
                            </div>
                            <p className={styles.assetDetail}>
                                {asset.ownership} &bull; {asset.expectedReturn}% expected return
                            </p>
                            <div className={styles.assetBalance}>
                                <span className={styles.balanceLabel}>Current Balance</span>
                                <span className={styles.balanceValue}>
                                    {formatBalance(asset.balance, asset.currency)}
                                </span>
                            </div>
                            <button
                                className={styles.deleteButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    deleteAsset(asset.accountId);
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {showAddAsset && (
                <AddAssetModal
                    defaultCountry={activeCountry}
                    onClose={() => setShowAddAsset(false)}
                    onAdded={() => {
                        setShowAddAsset(false);
                        refetch();
                    }}
                />
            )}

            {selectedAsset && (
                <AssetDetailModal
                    asset={selectedAsset}
                    goals={goals}
                    onClose={() => setSelectedAsset(null)}
                    onSaved={async (updated) => {
                        await updateAsset(updated.accountId, {
                            balance: updated.balance,
                            expectedReturn: updated.expectedReturn,
                            ownership: updated.ownership,
                            linkedGoalId: updated.linkedGoalId,
                        });
                        setSelectedAsset(null);
                    }}
                />
            )}
        </div>
    );
}
